import { isVideoSource, type RendererSource } from '@signa/renderer';
import {
  positionOf,
  startTimeline,
  stopTimeline,
  STOPPED_TIMELINE,
  type Timeline,
} from '@signa/shared';

/**
 * ビューアの再生操作の判断(#159)。
 *
 * 正本は [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md)。
 *
 * ## なぜシェルから切り出すのか
 *
 * [ADR-0019](../../../../docs/adr/0019-dev-foundation.md) により **DOM 構築は
 * jsdom で試さない。** 判断がシェルの DOM 操作に埋まっていると**検査できない** ——
 * 「静止画しか当たっていないのに再生ボタンが出る」といった間違いに気付けなくなる。
 *
 * **すべて純粋関数。** `<video>` の形(`currentTime` / `duration`)しか見ない。
 *
 * 位置をそろえる計算そのものは [`@signa/shared` の `playback.ts`](../../../../shared/src/playback.ts)
 * にある(S04 と共有 —— #123 / #159)。
 */

/** 割り当てのうち、再生の対象になるもの。 */
export interface Playable {
  readonly source: RendererSource;
}

/**
 * 再生・停止・同期が効く素材だけを取り出す。
 *
 * **静止画は除く**(#158)。`<img>` に `currentTime` は無く、
 * 触ると意味のない操作になる。
 *
 * **同じ要素は 1 度しか返さない**(#168)。共有すると 1 本の `<video>` が
 * 複数のセルに載るので、そのまま写すと**同じ要素に `play()` や
 * `currentTime = 0` をセルの数だけ掛ける。** 本数としても実態と合わない。
 */
export function playableOf(assignments: Iterable<Playable>): HTMLVideoElement[] {
  const videos = new Set<HTMLVideoElement>();
  for (const { source } of assignments) {
    if (isVideoSource(source)) videos.add(source);
  }
  return [...videos];
}

/** 操作 UI の出し方。 */
export interface ControlsState {
  /**
   * ボタンを出すか。
   *
   * **動画が 1 本も無ければ出さない。** 押しても何も起きないボタンは、
   * 「壊れている」と読める —— 静止画だけを当てた状態も含む(#158)。
   */
  readonly visible: boolean;
  /** 「再生」を押せるか。**再生中は押せない。** */
  readonly canPlay: boolean;
  /** 「停止」を押せるか。**止まっていれば押せない。** */
  readonly canPause: boolean;
  /**
   * 「同期」を押せるか。
   *
   * **止まっていても押せる。** 頭をそろえてから流し始める使い方があるため。
   */
  readonly canSync: boolean;
}

/**
 * 操作 UI の状態を決める。
 *
 * `playing` は**シェルが持つ**(利用者が押した結果)。`<video>` の `paused` を
 * 直接見ない —— **1 本でも再生に失敗すると食い違う**し、
 * ループの切れ目で一瞬 `paused` になる実装がある。
 */
export function controlsState(videoCount: number, playing: boolean): ControlsState {
  const visible = videoCount > 0;
  return {
    visible,
    canPlay: visible && !playing,
    canPause: visible && playing,
    canSync: visible,
  };
}

/**
 * 注意表示(「素材はサーバに送信されません」)を出すか(#159)。
 *
 * **素材を当てるまで。** 当てた後まで出し続ける必要はない(利用者の判断 /
 * 2026-08-10)—— [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md) は
 * 当初「ドロップ後も消さない」と決めていたが、社内での確認用途では
 * **場所を取るだけ**になる。
 *
 * **すべて解除したら戻す。** 消えたままだと「送信されたのでは」と読める ——
 * 元の決定が守ろうとしていたのはここである。
 */
export function showsNoUploadNotice(assignedCount: number): boolean {
  return assignedCount === 0;
}

/**
 * 再生の状態(#159)。
 *
 * **`playing` と `timeline` を一緒に動かす。** 別々に持つと
 * **「再生中なのに時間軸が止まっている」**という食い違いができ、
 * 次に「再生」を押したときに全部が頭へ巻き戻る。
 *
 * **不変条件: `playing === (timeline.startedAt !== null)`。**
 * ここを純関数にしてあるのは、[ADR-0019](../../../../docs/adr/0019-dev-foundation.md) により
 * シェルの状態遷移を jsdom で試せないため —— 上の食い違いは実際に踏んだ。
 */
export interface PlaybackState {
  readonly playing: boolean;
  readonly timeline: Timeline;
}

/** 止まっている状態。 */
export const STOPPED_PLAYBACK: PlaybackState = { playing: false, timeline: STOPPED_TIMELINE };

/**
 * 再生を始める(利用者が押した)。
 *
 * `fromStart` は「同期」—— **時間軸を先頭に戻す。**
 * そうでなければ止めた位置から続ける。
 */
export function startedPlayback(
  state: PlaybackState,
  now: number,
  fromStart: boolean,
): PlaybackState {
  return {
    playing: true,
    timeline: startTimeline(now, fromStart ? 0 : state.timeline.baseTime),
  };
}

/** 止める。**位置は保つ**(「再生」で続きから)。 */
export function stoppedPlayback(state: PlaybackState, now: number): PlaybackState {
  return { playing: false, timeline: stopTimeline(state.timeline, now) };
}

/**
 * 自動再生で始まった(素材を当てた直後)。
 *
 * **すでに走っていればそのまま。** 走っていなければ時間軸を動かす ——
 * ここを忘れると**見張りが何もせず、次の「再生」で全部が頭へ戻る。**
 */
export function autoStartedPlayback(state: PlaybackState, now: number): PlaybackState {
  if (state.timeline.startedAt !== null) return { ...state, playing: true };
  return { playing: true, timeline: startTimeline(now, state.timeline.baseTime) };
}

/**
 * いま当てた素材を、走っている時間軸に合わせるべき位置。
 *
 * **走っていなければ `null`**(合わせる必要がない)。合わせないと
 * **当てた順にずれる** —— #123 で報告された症状そのもの。
 */
export function joinPositionOf(state: PlaybackState, now: number): number | null {
  return state.timeline.startedAt === null ? null : positionOf(state.timeline, now);
}
